import { useApplicationUpdater } from './useApplicationUpdater';
import { AnyForm } from './useFormField';

/**
 * Returns a function that ensures a form stub exists in `information.properties.forms`.
 * If a form with (formType, propertyGuid) already exists, the cache is left unchanged.
 *
 * Used by Budget/SpecSheet page components to create a fresh form stub when the user
 * chooses to create a new form rather than loading a saved one.
 *
 * @param propertyGuid - GUID of the property this form belongs to
 * @param formType     - form type discriminant (e.g. 'Budget', 'Fix-Flip', 'Land Budget', 'Spec Sheet')
 * @param createStub   - factory that returns the initial form stub
 * @returns            a no-arg function; call it inside a useEffect
 *
 * @example
 * const ensureStub = useEnsureFormStub(propertyGuid, 'Budget', () => ({
 *     id: 0, guid: entryGuid, type: 'Budget', name: '', propertyGuid, values: {} as IBudgetConstruction['values'],
 * }));
 * useEffect(() => { if (!savedBudgetSelected) ensureStub(); }, [savedBudgetSelected]);
 */
export function useEnsureFormStub(
    propertyGuid: string,
    formType: string,
    createStub: () => AnyForm
): () => void {
    const update = useApplicationUpdater();

    return () => {
        update((old) => {
            const forms = old.information?.properties?.forms ?? [];
            const exists = forms.some((f) => f.type === formType && f.propertyGuid === propertyGuid);
            if (exists) return old;
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: [...forms, createStub()],
                    },
                },
            };
        });
    };
}
